"use client";

import React from "react";
import { ThemeUICSSObject } from "theme-ui";

import {
  ThemeColors,
  ThemeFonts,
  ThemeFontSizes,
  ThemeFontWeights,
  ThemeLetterSpaces
} from "../foundations";
import type { BoxSx } from "../foundations/Box";

interface InheritedStylesContext {
  color: ThemeColors;
  fontFamily: ThemeFonts;
  fontSize: ThemeFontSizes;
  fontWeight?: ThemeFontWeights;
  lineHeight?: ThemeFontSizes;
  letterSpacing?: ThemeLetterSpaces;
  textRendering?: ThemeUICSSObject["textRendering"];
  overflowWrap: ThemeUICSSObject["overflowWrap"];
}

const InheritedStylesContext = React.createContext<InheritedStylesContext>({
  color: "text-default",
  fontFamily: "body",
  fontSize: "font-size-4",
  lineHeight: "leading-2",
  overflowWrap: "break-word"
});

export const InheritedStylesProvider: React.FC<{
  children: React.ReactNode;
  styleContext: InheritedStylesContext;
}> = ({ children, styleContext }) => {
  return (
    <InheritedStylesContext.Provider value={styleContext}>
      {children}
    </InheritedStylesContext.Provider>
  );
};
interface MergeStylesResult {
  newInheritedStylesContext: InheritedStylesContext;
  mergedSx: BoxSx;
}

export const useInheritedStylesContext = () =>
  React.useContext(InheritedStylesContext);

/**
 * Hook to merge styles from sx object and inheritedStylesContext
 *
 * @param {BoxSx} sx - The styles to merge with the context styles.
 * @returns {MergeStylesResult} {newInheritedStylesContext: InheritedStylesContext, mergedSx: BoxSx}
 * @example
 *
 * const ExampleComponent = ({children, sx}) => {
 *  const { newInheritedStylesContext, mergedSx } = useMergeStyles(sx);
 *  return (
 *    <InheritedStylesProvider styleContext={newInheritedStylesContext}>
 *      <Box sx={mergedSx}>
 *       {children}
 *      </Box>
 *    </InheritedStylesProvider>
 *  )
 * }
 *
 */
export const useMergeStyles = (sx: BoxSx): MergeStylesResult => {
  const currentInheritedStylesContext = React.useContext(
    InheritedStylesContext
  );

  const newInheritedStylesContext = React.useMemo(() => {
    return {
      color: sx?.color || currentInheritedStylesContext.color,
      fontFamily: sx?.fontFamily || currentInheritedStylesContext.fontFamily,
      fontSize: sx?.fontSize || currentInheritedStylesContext.fontSize,
      fontWeight: sx?.fontWeight || currentInheritedStylesContext.fontWeight,
      lineHeight: sx?.lineHeight || currentInheritedStylesContext.lineHeight,
      letterSpacing:
        sx?.letterSpacing || currentInheritedStylesContext.letterSpacing,
      overflowWrap:
        sx?.overflowWrap || currentInheritedStylesContext.overflowWrap,
      textRendering:
        sx?.textRendering || currentInheritedStylesContext.textRendering
    };
  }, [
    currentInheritedStylesContext.color,
    currentInheritedStylesContext.fontFamily,
    currentInheritedStylesContext.fontSize,
    currentInheritedStylesContext.fontWeight,
    currentInheritedStylesContext.letterSpacing,
    currentInheritedStylesContext.lineHeight,
    currentInheritedStylesContext.overflowWrap,
    currentInheritedStylesContext.textRendering,
    sx?.color,
    sx?.fontFamily,
    sx?.fontSize,
    sx?.fontWeight,
    sx?.letterSpacing,
    sx?.lineHeight,
    sx?.overflowWrap,
    sx?.textRendering
  ]);

  // remove any inheritedProperties on the sx object that are explicly set to null or undeinfed - I.e. {fontFamily: undefined} - This will avoid breaking the style cascade
  cleanSx(sx);

  const mergedSx = {
    ...newInheritedStylesContext,
    ...sx
  };
  return {
    newInheritedStylesContext,
    mergedSx
  };
};

const cleanSx = (sx: BoxSx) => {
  inheritedProperties.forEach((inheritedProperty) => {
    if (
      sx.hasOwnProperty(inheritedProperty) &&
      (sx[inheritedProperty] === undefined || sx[inheritedProperty] === null)
    ) {
      delete sx[inheritedProperty];
    }
  });
};

const inheritedProperties: (keyof InheritedStylesContext)[] = [
  "color",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "textRendering",
  "overflowWrap"
];
